import React from "react";
import { motion } from "framer-motion";

/**
 * Boardy mascot component. Enforces the app-wide standard sizes:
 *
 *   size="standard" (default) → 75px wide on mobile, 110px wide on desktop
 *   size="hero"                → 110px wide on mobile, 160px wide on desktop
 *
 * When `src` is provided the illustrated asset is rendered with h-auto so the
 * native aspect ratio is preserved. When `src` is not provided the ASCII
 * fallback is rendered at the same OUTER dimensions (aspect-square) so that
 * the layout of a page does not shift once the real asset is plugged in.
 */
export default function MascotPlaceholder({
  emotion = "happy",
  size = "standard",
  className = "",
  src,
  alt = "Boardy, la mascotte di ExchangePlan",
}) {
  const sizeClass =
    size === "hero"
      ? "w-[110px] sm:w-[160px]"
      : "w-[75px] sm:w-[110px]";

  if (src) {
    return (
      <motion.div
        data-testid="mascot-image"
        data-mascot-size={size}
        className={`shrink-0 ${sizeClass} ${className}`}
        animate={{ y: [0, -8, 0] }}
        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
      >
        <img
          src={src}
          alt={alt}
          className="block w-full h-auto object-contain"
          draggable={false}
        />
      </motion.div>
    );
  }

  // ASCII fallback keeps the same width/height footprint so pages don't reflow
  // when the real asset is added later.
  const heightClass =
    size === "hero"
      ? "h-[110px] sm:h-[160px]"
      : "h-[75px] sm:h-[110px]";
  const fontClass = size === "hero" ? "text-4xl sm:text-5xl" : "text-2xl sm:text-3xl";
  const emoticons = {
    happy: "◕‿◕",
    thinking: "◔_◔",
    celebrating: "＼(^o^)／",
    resting: "-‿-",
  };

  return (
    <motion.div
      data-testid="mascot-placeholder"
      data-mascot-size={size}
      className={`shrink-0 ${sizeClass} ${heightClass} ${className} rounded-full border-2 border-dashed border-terracotta/40 bg-terracotta/10 flex items-center justify-center font-display font-bold text-terracotta ${fontClass} relative`}
      animate={{ y: [0, -8, 0] }}
      transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
    >
      <span aria-hidden>{emoticons[emotion] || emoticons.happy}</span>
      <span className="sr-only">Mascot placeholder ({emotion})</span>
      <span className="absolute -bottom-2 -right-1 text-[10px] uppercase tracking-widest bg-sand/90 text-ink px-2 py-0.5 rounded-full font-sans font-medium">
        mascot
      </span>
    </motion.div>
  );
}
